import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  AccountBalance as BankIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

import { RootState } from '../../store/store';
import {
  selectDashboardStats,
  selectRecentInvoices,
  selectRecentABOFiles,
  selectDashboardLoading,
  selectFormattedAmounts,
  selectCurrentUser,
} from '../../store/selectors/dashboardSelectors';
import {
  OptimizedStatsCard,
  OptimizedProgressCard,
  OptimizedRecentInvoices,
  OptimizedRecentABOFiles,
} from './OptimizedDashboardComponents';
import { useOptimizedDashboard } from '../../hooks/useOptimizedDashboard';
import { useMobileLayout } from '../../hooks/useMobileLayout';

const OptimizedDashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const { spacing, typography } = useMobileLayout();
  
  // Selectors
  const stats = useSelector(selectDashboardStats);
  const recentInvoices = useSelector(selectRecentInvoices);
  const recentABOFiles = useSelector(selectRecentABOFiles);
  const isLoading = useSelector(selectDashboardLoading);
  const formattedAmounts = useSelector(selectFormattedAmounts);
  const user = useSelector(selectCurrentUser);
  
  // Custom hook for data fetching
  const { refreshData } = useOptimizedDashboard();

  // Initial data load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Navigation handlers
  const handleViewInvoices = useCallback(() => {
    navigate('/invoices');
  }, [navigate]);

  const handleViewABO = useCallback(() => {
    navigate('/abo');
  }, [navigate]);

  const handleViewUsers = useCallback(() => {
    navigate('/users');
  }, [navigate]);

  const handleRefresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  return (
    <Box sx={{ p: { xs: spacing.xs, sm: spacing.sm, md: spacing.sm, lg: spacing.md }, maxWidth: '100%', overflow: 'hidden' }}>
      {/* Welcome Section */}
      <Paper 
        sx={{ 
          p: { xs: spacing.sm, sm: spacing.md, md: spacing.md, lg: spacing.lg }, 
          mb: { xs: spacing.sm, sm: spacing.md, md: spacing.sm, lg: spacing.lg }, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography 
            variant={isMobile ? "h4" : "h3"} 
            sx={{ 
              mb: 1, 
              fontWeight: 700,
              fontSize: typography.h3
            }}
          >
            Vítejte zpět, {user?.firstName || 'Uživateli'}!
          </Typography>
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            sx={{ 
              opacity: 0.9, 
              mb: { xs: spacing.sm, md: spacing.md },
              fontSize: typography.body1
            }}
          >
            Zde je přehled vašich faktur a ABO souborů
          </Typography>
          <Box sx={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              size={isMobile ? "medium" : "large"}
              onClick={handleViewInvoices}
              sx={{ 
                background: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: typography.button,
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              Zobrazit všechny faktury
            </Button>
            <Button 
              variant="outlined" 
              size={isMobile ? "medium" : "large"}
              onClick={handleRefresh}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                fontSize: typography.button,
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  background: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              {isLoading ? 'Načítání...' : 'Obnovit'}
            </Button>
          </Box>
        </Box>
        <Box 
          sx={{ 
            position: 'absolute', 
            top: -50, 
            right: -50, 
            width: 200, 
            height: 200, 
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            zIndex: 1
          }} 
        />
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={{ xs: 2, md: 2, lg: 3 }} sx={{ mb: { xs: 3, md: 3, lg: 4 }, width: '100%', margin: 0 }}>
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <OptimizedStatsCard
            title="Celkem faktur"
            value={isLoading ? '...' : stats.totalInvoices}
            subtitle={`+${stats.newInvoices} nových`}
            icon={<ReceiptIcon sx={{ fontSize: 100 }} />}
            backgroundColor="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <OptimizedStatsCard
            title="Celková hodnota"
            value={isLoading ? '...' : `${formattedAmounts.totalAmountFormatted} Kč`}
            subtitle={`Průměrně ${formattedAmounts.averageAmountFormatted} Kč/faktura`}
            icon={<MoneyIcon sx={{ fontSize: 100 }} />}
            backgroundColor="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <OptimizedStatsCard
            title="Zaplacené"
            value={isLoading ? '...' : stats.paidInvoices}
            subtitle={`${stats.paidPercentage}% všech`}
            icon={<CheckCircleIcon sx={{ fontSize: 100 }} />}
            backgroundColor="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={3}>
          <OptimizedStatsCard
            title="ABO soubory"
            value={isLoading ? '...' : recentABOFiles.length}
            subtitle={`${recentABOFiles.filter(f => f.status === 'sent_to_bank').length} odeslaných`}
            icon={<BankIcon sx={{ fontSize: 100 }} />}
            backgroundColor="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
          />
        </Grid>
      </Grid>

      {/* Progress and Quick Actions */}
      <Grid container spacing={{ xs: 2, md: 2, lg: 3 }} sx={{ mb: { xs: 3, md: 3, lg: 4 }, width: '100%', margin: 0 }}>
        <Grid item xs={12} md={12} lg={6}>
          <OptimizedProgressCard
            title="Průběh zpracování"
            stats={stats}
          />
        </Grid>

        <Grid item xs={12} md={12} lg={6}>
          <Paper sx={{ p: { xs: 2, md: 2, lg: 3 }, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Rychlé akce
            </Typography>
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={6} sm={3} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleViewABO}
                  sx={{ height: { xs: 50, md: 60 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Vytvořit ABO
                </Button>
              </Grid>
              <Grid item xs={6} sm={3} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleViewInvoices}
                  sx={{ height: { xs: 50, md: 60 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Export faktur
                </Button>
              </Grid>
              <Grid item xs={6} sm={3} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleViewInvoices}
                  sx={{ height: { xs: 50, md: 60 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Zobrazit faktury
                </Button>
              </Grid>
              <Grid item xs={6} sm={3} md={6} lg={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleViewUsers}
                  sx={{ height: { xs: 50, md: 60 }, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
                >
                  Správa uživatelů
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Grid container spacing={{ xs: 2, md: 2, lg: 3 }} sx={{ width: '100%', margin: 0 }}>
        <Grid item xs={12} md={12} lg={6}>
          <OptimizedRecentInvoices
            invoices={recentInvoices}
            onViewAll={handleViewInvoices}
          />
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button 
              size="small" 
              endIcon={<ArrowForwardIcon />}
              onClick={handleViewInvoices}
            >
              Zobrazit všechny
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={12} lg={6}>
          <OptimizedRecentABOFiles
            aboFiles={recentABOFiles}
            onViewAll={handleViewABO}
          />
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button 
              size="small" 
              endIcon={<ArrowForwardIcon />}
              onClick={handleViewABO}
            >
              Zobrazit všechny
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OptimizedDashboard;
